import {ObjectRepository} from "@api/repositories/ObjectRepository";
import { isAxiosError } from "@api/helpers/errorHelpers";
import { Store } from "vuex";
import { ElementConverter } from "@api/repositories/types/ElementConverter";
import { Element } from "@api/models/Element";
import { MultiLanguageObject } from "@api/models/objects/MultiLanguageObject";
import { ObjectDataParamsInterface } from "@api/repositories/interfaces/ObjectDataParamsInterface";
import { ObjectFindParameters } from "@api/repositories/interfaces/ObjectFindParameters";
import {QueryParametersInterface} from "@api/repositories/interfaces/QueryParametersInterface";

export class ObjectsService {
    constructor(
        private readonly repository: ObjectRepository,
        private readonly store: Store<any>
    ) { }

    list(schema: string, query: QueryParametersInterface = {}, converter?: ElementConverter) {
        try {
            return this.repository.query(schema, query, converter);
        }
        catch (e) {
            this.store.dispatch('reportException', e);
            return [];
        }
    }

    /**
     * getting element for the given languages
     * @param parameters
     * @param languages
     */
    async getDataForAllLanguages<T extends Element>(
        parameters: ObjectFindParameters<T>,
        languages: string[]
    ): Promise<{ [lang: string]: T } | null> {
        try {
            return await this.repository.getByLanguages<T>(parameters, languages);
        }
        catch(e) {
            const error = e;

            if (isAxiosError<any>(error) && error.response?.status === 404) {
                return null;
            }
            else {
                this.store.dispatch('reportException', e, { root: true });
            }
        }

        return null;
    }

    async create<T extends Element>(schemaId: string, data: MultiLanguageObject<T>, converter?: ElementConverter): Promise<string> {
        try {
            const {id} = await this.repository.create<T>(schemaId, data.userLangForSave, converter);

            if (
                id &&
                data.languageService.languagesWithoutUsersOne.length > 0 &&
                data.schemaSettings.localizableFields.length > 0
            )
            {
                await this.update<T>(schemaId, id, data, true);
            }

            return id;
        }
        catch (e) {
            this.store.dispatch('reportException', e);
            return '';
        }
    }

    async update<T extends Element>(
        schemaId: string,
        id: string,
        data: MultiLanguageObject<T>,
        onlyLocalizedValues = false
    ): Promise<ObjectDataParamsInterface | undefined> {
        try {
            const dataForUpdate: ObjectDataParamsInterface = {
                languages: data.languagesData
            }

            if (!onlyLocalizedValues) {
                dataForUpdate.fields = data.userLangForSave;
            }

            return await this.repository.updateV2(schemaId, id, dataForUpdate);
        }
        catch (e) {
            this.store.dispatch('reportException', e);
            return undefined;
        }
    }
}
